import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { addDays, subDays, format } from "date-fns";

const BookingContext = createContext(null);

/**
 * Centralized Booking State Management
 * 
 * State persists across:
 * - Category navigation
 * - PLP → Quick View → PDP
 * - Page refreshes (sessionStorage)
 * 
 * Duration is ONLY set on PDP - never pre-selected elsewhere
 */
export function BookingProvider({ children }) {
  // Core state
  const [eventDate, setEventDateState] = useState(() => {
    const saved = sessionStorage.getItem("mahra_event_date");
    return saved ? new Date(saved) : null;
  });
  
  // Duration is null until explicitly selected on PDP
  const [rentalDuration, setRentalDurationState] = useState(() => {
    const saved = sessionStorage.getItem("mahra_rental_duration");
    return saved ? parseInt(saved, 10) : null;
  });
  
  // Dispatch buffer (1 or 2 days before event)
  const [dispatchBuffer, setDispatchBuffer] = useState(2);
  
  // UI state
  const [showDateModal, setShowDateModal] = useState(false);
  const [pendingCategoryNav, setPendingCategoryNav] = useState(null); // Store category to navigate after date selection
  
  // Derived dates — Delivery = Event - 1, Rental End = Delivery + Duration - 1
  const dispatchDate = eventDate ? subDays(eventDate, 1) : null;
  const returnDate = dispatchDate && rentalDuration ? addDays(dispatchDate, rentalDuration - 1) : null;
  
  // Persist to sessionStorage
  useEffect(() => {
    if (eventDate) {
      sessionStorage.setItem("mahra_event_date", eventDate.toISOString());
    } else {
      sessionStorage.removeItem("mahra_event_date");
    }
  }, [eventDate]);
  
  useEffect(() => {
    if (rentalDuration !== null) {
      sessionStorage.setItem("mahra_rental_duration", String(rentalDuration));
    } else {
      sessionStorage.removeItem("mahra_rental_duration");
    }
  }, [rentalDuration]);
  
  // Set event date (from modal or PDP)
  const setEventDate = useCallback((date) => {
    setEventDateState(date);
  }, []);
  
  // Set duration (ONLY from PDP)
  const setRentalDuration = useCallback((duration) => {
    setRentalDurationState(duration);
  }, []);
  
  // Clear all booking state
  const clearBookingState = useCallback(() => {
    setEventDateState(null);
    setRentalDurationState(null);
    sessionStorage.removeItem("mahra_event_date");
    sessionStorage.removeItem("mahra_rental_duration");
  }, []);
  
  // Clear only the date (keeps duration preference if set)
  const clearEventDate = useCallback(() => {
    setEventDateState(null);
    sessionStorage.removeItem("mahra_event_date");
  }, []);
  
  // Open date modal (optionally with a pending navigation)
  const openDateModal = useCallback((pendingNav = null) => {
    setPendingCategoryNav(pendingNav);
    setShowDateModal(true);
  }, []);
  
  // Close date modal
  const closeDateModal = useCallback(() => {
    setShowDateModal(false);
    setPendingCategoryNav(null);
  }, []);
  
  // Format helper for date bar
  const getDateBarText = useCallback(() => {
    if (!eventDate) return null;
    
    const dateStr = format(eventDate, "d MMMM yyyy");
    
    if (rentalDuration && returnDate) {
      return {
        full: `${dateStr} · ${rentalDuration}-Day Rental · Returns ${format(returnDate, "d MMMM")}`,
        dateOnly: dateStr,
        hasDuration: true
      };
    }
    
    return {
      full: dateStr,
      dateOnly: dateStr,
      hasDuration: false
    };
  }, [eventDate, rentalDuration, returnDate]);
  
  // Check if we have date selected (for availability checks)
  const hasDateSelected = eventDate !== null;
  
  // Check if we have full booking info (date + duration)
  const hasFullBookingInfo = eventDate !== null && rentalDuration !== null;
  
  return (
    <BookingContext.Provider value={{
      // Core state
      eventDate,
      rentalDuration,
      dispatchBuffer,
      
      // Derived dates
      dispatchDate,
      returnDate,
      
      // State setters
      setEventDate,
      setRentalDuration,
      setDispatchBuffer,
      
      // Clear functions
      clearBookingState,
      clearEventDate,
      
      // Modal control
      showDateModal,
      openDateModal,
      closeDateModal,
      pendingCategoryNav,
      
      // Helpers
      getDateBarText,
      hasDateSelected,
      hasFullBookingInfo,
      dispatchBuffer,
    }}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
}

// Re-export for backward compatibility with existing EventDateContext usage
export function useEventDate() {
  const booking = useBooking();
  return {
    eventDate: booking.eventDate,
    setEventDate: booking.setEventDate,
    rentalDuration: booking.rentalDuration || 4,
    setRentalDuration: booking.setRentalDuration,
    showPicker: booking.showDateModal,
    setShowPicker: (show) => show ? booking.openDateModal() : booking.closeDateModal(),
    clearDate: booking.clearEventDate,
    dispatchBuffer: booking.dispatchBuffer || 2,
  };
}
